import { useState } from "react";
import { observer } from "mobx-react-lite";
import { createUser } from "../../service/api_service";
import {
  signup,
  authenticateUser,
  verifyAccount,
  resendConfirmation,
} from "./cognito";
import { useNavigate, useParams } from "react-router-dom";
import { config } from "../../utils/config";
import { Box, Card, Divider, Stack, Typography } from "@mui/material";
import { Constants } from "../../utils/constants";
import { DefaultButton, GoogleLoginWidget, MainButton, MainInput, SuccessButton } from "..";
import { isValidEmail } from "../../utils/stringFormat";

function Signup({ authStore, navStore }) {
  const [resending, setResending] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState(false);

  const [step, setStep] = useState(1);

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [company, setCompany] = useState("");

  const [newPassword, setNewPassword] = useState("");
  const [newPassword2, setNewPassword2] = useState("");
  const [user, setUser] = useState(null);
  const [verificationCode, setVerificationCode] = useState("");

  const navigate = useNavigate();
  const { option } = useParams();

  const onEmailFinish = async (e) => {
    e.preventDefault();
    setEmailError(false);
    if (!isValidEmail(email)) {
      setEmailError(true);
      setError("Email is invalid");
      return;
    }
    setStep(2);
  };

  const onFinish = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      if (newPassword !== newPassword2) {
        setError("Password mismatch");
        setSubmitting(false);
      } else {
        try {
          const result = await signup(
            email,
            newPassword,
            email,
            firstName,
            lastName
          );
          await createUser(email, "FREE", company);
          setUser(result.user);
        } catch (e) {
          setError(e.message);
          setSubmitting(false);
        }
      }
      setSubmitting(false);
    } catch (e) {
      setSubmitting(false);
      setError(e.message);
    }
  };

  const autoLogin = async () => {
    await authenticateUser(email, newPassword);
  };

  const resendCode = async () => {
    setResending(true);
    try {
      await resendConfirmation(email);
    } finally {
      setResending(false);
    }
  };

  const verify = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      if (!verificationCode) {
        setError("Verification code is required");
        setSubmitting(false);
        return;
      }
      await verifyAccount(user.username, verificationCode);
      await autoLogin();
      navigate("/");
    } catch (e) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const onGoogleLogin = () => {
    const URL = `${config.AUTH_URL}/authorize?identity_provider=Google&redirect_uri=${config.REDIRECT_URI}&response_type=CODE&client_id=${config.COGNITO_CLIENT_ID}&scope=openid%20email%20profile`;
    window.location.replace(URL);
  };

  return (
    <Box
      sx={{
        padding: "auto",
        display: "flex",
        alignItems: "start",
        justifyContent: "center",
        pt: "3rem",
      }}
    >
      <Card
        sx={{
          padding: "3rem 2.5rem",
        }}
      >
        <Stack
          alignItems="center"
          justifyContent={"center"}
          spacing={`calc(${Constants.Styles.spacing} * 6)`}
        >
          <Typography sx={{
            fontSize: Constants.Styles.fontSize["3xl"],
            fontWeight: Constants.Styles.fontWeight.semiBold,
            lineHeight: Constants.Styles.lineHeight["3xl"],
          }}>Create your free account</Typography>
          <Box>
            {user == null && step === 1 && (
              <Stack
                spacing={2}
                sx={{
                  minWidth: { xs: 300, sm: 360, },
                }}
                justifyContent={"center"}
              >
                <GoogleLoginWidget onClick={onGoogleLogin} />

                <Divider>
                  OR
                </Divider>

                <Box
                  key={"basic"}
                  name="basic"
                  component={"form"}
                  noValidate
                  onSubmit={onEmailFinish}
                >
                  <MainInput
                    name="email"
                    label="Email"
                    text="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    error={emailError}
                  />

                  {error && <Typography sx={{
                    fontSize: Constants.Styles.fontSize.sm,
                    color: Constants.Colors.light.destructive,
                    mt: 2,
                  }}>{error}</Typography>}
                  <Box sx={{ mt: 3, }}>
                    <SuccessButton
                      text={"Continue"}
                      type="submit"
                      size="medium"
                    />
                  </Box>
                </Box>
              </Stack>
            )}

            {user == null && step === 2 && (
              <Box
                name="basic"
                component={"form"}
                noValidate
                onSubmit={onFinish}
              >
                <Stack
                  spacing={2}
                  sx={{
                    minWidth: { xs: 300, sm: 360, },
                  }}
                  justifyContent={"center"}
                >
                  <MainInput
                    label="First name"
                    name="name"
                    type="text"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                  />
                  <MainInput
                    label="Last name"
                    name="lastname"
                    type="text"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                  />
                  <MainInput
                    label="Company"
                    name="company"
                    type="text"
                    value={company}
                    onChange={(e) => setCompany(e.target.value)}
                  />
                  <MainInput
                    label="New Password"
                    name="newPassword"
                    type="password"
                    isSecure={true}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                  />
                  <MainInput
                    label="Re-enter Password"
                    name="newPassword1"
                    type="password"
                    isSecure={true}
                    value={newPassword2}
                    onChange={(e) => setNewPassword2(e.target.value)}
                  />
                  {error && <Typography sx={{
                    fontSize: Constants.Styles.fontSize.sm,
                    color: Constants.Colors.light.destructive,
                    mt: 2,
                  }}>{error}</Typography>}
                  <Box sx={{ mt: 3, }}>
                    <SuccessButton
                      text={"Signup"}
                      type="submit"
                      size="medium"
                      isSubmitting={submitting}
                    />
                  </Box>
                </Stack>
              </Box>
            )}
            {user != null && (
              <Box
                name="basic"
                component={"form"}
                noValidate
                onSubmit={verify}
              >
                <Stack
                  spacing={2}
                  sx={{
                    minWidth: { xs: 300, sm: 360, },
                  }}
                  justifyContent={"center"}
                >
                  <Typography
                    sx={{
                      fontSize: Constants.Styles.fontSize.base,
                      fontWeight: Constants.Styles.fontWeight.normal,
                    }}
                  >
                    We've sent a code to your email. Please enter the verification
                    code.
                  </Typography>
                  <MainInput
                    name="verificationCode"
                    label="Verification code"
                    type="text"
                    value={verificationCode}
                    onChange={(e) => setVerificationCode(e.target.value)}
                  />

                  {error && <Typography sx={{
                    fontSize: Constants.Styles.fontSize.sm,
                    color: Constants.Colors.light.destructive,
                    mt: 2,
                  }}>{error}</Typography>}
                  <Box sx={{ mt: 0, }}>
                    <SuccessButton
                      text={"Verify"}
                      type="submit"
                      size="medium"
                      isSubmitting={submitting}
                    />
                  </Box>
                  <MainButton
                    text={"Resend code"}
                    type="button"
                    size="medium"
                    onClick={() => resendCode()}
                    isSubmitting={resending}
                  />
                </Stack>
              </Box>
            )}
          </Box>
        </Stack>
      </Card>
    </Box>
  );
}

export default observer(Signup);
